import * as React from "react";
import { AuthPage as MUIAuthPage, type AuthProps } from "@refinedev/mui";
import { Link, useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";

import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";

import { PhoneAuth } from "./PhoneAuth";
import { EmailVerification } from "./EmailVerification";
import { getInvisibleRecaptcha, getlostFirebaseAuth } from "../../utils/auth/firebaseConfig";
import { isOnboardingRequired } from "../../utils/auth/getlostAuthProvider";
import { isSignInWithEmailLink } from "firebase/auth";

// Email Collection Form Component
const EmailCollectionForm: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const [email, setEmail] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [emailSent, setEmailSent] = React.useState(false);

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSendVerification = async () => {
    if (!email || !validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await getlostFirebaseAuth.sendEmailVerificationCode(email);
      setEmailSent(true);
    } catch (error: any) {
      console.error("Error sending verification email:", error);
      setError(error?.message || "Failed to send verification email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check if email is verified
      await getlostFirebaseAuth.verifyEmailAndUpdate(email, "");
      onSuccess();
    } catch (error: any) {
      console.error("Error checking verification:", error);
      setError(error?.message || "Email not verified yet. Please check your email.");
    } finally {
      setLoading(false);
    }
  };

  const handleProviderLink = async (provider: 'google' | 'apple') => {
    setLoading(true);
    setError(null);
    
    try {
      // Link the Google/Apple account to get email
      await getlostFirebaseAuth.linkProvider(provider);
      
      // Verify that the user now has an email after linking
      const hasEmail = await getlostFirebaseAuth.checkUserHasEmail();
      
      if (!hasEmail) {
        // Provider linked successfully but didn't provide email (e.g., Apple Hide My Email)
        // Just stay on the form so user can enter email manually
        // Don't set it as an error, just inform the user
        setLoading(false);
        return;
      }
      
      // Email confirmed, proceed
      onSuccess();
    } catch (error: any) {
      console.error(`Error linking ${provider}:`, error);
      
      // Only show errors for actual failures (not missing email)
      if (error.message !== "No email received from provider") {
        setError(error?.message || `Failed to connect with ${provider}. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, textAlign: "center", color: "primary.main" }}>
        Email Verification Required
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "center" }}>
        {!emailSent 
          ? "We need to verify your email address to complete your account setup."
          : `We've sent a verification link to ${email}. Please check your email and click the link, then come back here and click 'I've Verified My Email'.`
        }
      </Typography>

      {error && (
        <Box sx={{ p: 2, bgcolor: "error.light", borderRadius: 1 }}>
          <Typography variant="body2" color="error.dark">
            {error}
          </Typography>
        </Box>
      )}

      {!emailSent ? (
        <>
          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSendVerification(); }}>
            <TextField
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              fullWidth
              disabled={loading}
              placeholder="your.email@example.com"
              autoFocus
              error={email.length > 0 && !validateEmail(email)}
              helperText={email.length > 0 && !validateEmail(email) ? "Invalid email address" : ""}
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              onClick={handleSendVerification}
              disabled={loading || !email || !validateEmail(email)}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : "Send Verification Email"}
            </Button>
          </Box>

          {/* Divider */}
          <Box sx={{ my: 2, display: "flex", alignItems: "center" }}>
            <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
            <Typography variant="body2" sx={{ mx: 2, color: "text.secondary" }}>
              or
            </Typography>
            <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
          </Box>

          {/* Provider buttons */}
          <Button
            variant="outlined"
            onClick={() => handleProviderLink('google')}
            disabled={loading}
            fullWidth
            startIcon={<GoogleIcon />}
            sx={{ mb: 1 }}
          >
            Connect with Google
          </Button>

          <Button
            variant="outlined"
            onClick={() => handleProviderLink('apple')}
            disabled={loading}
            fullWidth
            startIcon={<AppleIcon />}
          >
            Connect with Apple
          </Button>
        </>
      ) : (
        <Box>
          <Button
            variant="contained"
            onClick={handleCheckVerification}
            disabled={loading}
            fullWidth
            sx={{ mb: 1 }}
          >
            {loading ? <CircularProgress size={24} /> : "I've Verified My Email"}
          </Button>

          <Button
            variant="text"
            onClick={() => {
              setEmailSent(false);
              setError(null);
            }}
            disabled={loading}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
          >
            Use Different Email
          </Button>
          
          <Button
            variant="text"
            onClick={handleSendVerification}
            disabled={loading}
            fullWidth
            size="small"
          >
            Resend Verification Email
          </Button>
        </Box>
      )}
    </Box>
  );
};

const authWrapperProps = {
  style: {
    background:
      "radial-gradient(50% 50% at 50% 50%,rgba(255, 255, 255, 0) 0%,rgba(0, 0, 0, 0.5) 100%),url('images/background-getlost.png')",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundColor: "#000",
  },
};

export const AuthPage: React.FC<AuthProps> = ({ type, formProps }) => {
  const navigate = useNavigate();
  const [authMode, setAuthMode] = React.useState<"phone" | "email">(() => {
    // Check if this is a magic link callback (email link sign-in)
    if (isSignInWithEmailLink(getlostFirebaseAuth.auth, window.location.href)) {
      return "email"; // Automatically switch to email mode to process the magic link
    }
    return "phone";
  });
  const [needsEmailVerification, setNeedsEmailVerification] = React.useState(false);
  const [isAlreadyAuthenticated, setIsAlreadyAuthenticated] = React.useState(false);

  // Check if user is already authenticated when on register page
  React.useEffect(() => {
    if (type === "register") {
      const checkAuth = async () => {
        try {
          const user = getlostFirebaseAuth.auth?.currentUser;
          if (user) {
            console.log("User already authenticated on onboarding page:", user.uid);
            setIsAlreadyAuthenticated(true);
            
            // Check if they have email
            const hasEmail = await getlostFirebaseAuth.checkUserHasEmail();
            if (!hasEmail) {
              console.log("User has no email - showing email collection");
              setNeedsEmailVerification(true);
            }
            // If user has email, they'll see the onboarding form (isAlreadyAuthenticated=true)
          }
        } catch (error) {
          console.error("Error checking auth status:", error);
        }
      };
      
      checkAuth();
    }
  }, [type]);

  const handleAuthSuccess = async () => {
    // Check if user has email after successful authentication
    try {
      const hasEmail = await getlostFirebaseAuth.checkUserHasEmail();
      if (!hasEmail) {
        // Show email collection instead of navigating
        setNeedsEmailVerification(true);
        return;
      }

      // Email is present; ensure the admin session is established before
      // navigating. If the backend returns 404 (no users row), route to
      // /onboarding instead of letting the protected route's
      // <Authenticated> check bounce the user to /login.
      try {
        await getlostFirebaseAuth.establishAdminSession();
        navigate("/");
      } catch (sessionErr: any) {
        if (isOnboardingRequired(sessionErr)) {
          navigate("/onboarding");
          return;
        }
        throw sessionErr;
      }
    } catch (error) {
      console.error("Error completing auth success:", error);
      navigate("/");
    }
  };

  const handleNeedsOnboarding = () => {
    console.log("User needs onboarding - redirecting to onboarding page");
    console.log("Current pathname:", window.location.pathname);
    console.log("Navigating to /onboarding...");
    navigate("/onboarding");
    console.log("Navigation called - checking if it worked");
  };

  const handleEmailVerified = async () => {
    // After user clicks "I've Verified My Email":
    // 1) Reload Firebase user and force fresh ID token
    // 2) Ensure email exists
    // 3) Establish admin session; on 404 navigate to onboarding, else to home
    try {
      // Reload current user to reflect latest verification state
      await getlostFirebaseAuth.auth?.currentUser?.reload?.();
      // Force refresh ID token to avoid using a stale backend session
      await getlostFirebaseAuth.auth?.currentUser?.getIdToken?.(true);

      const hasEmail = await getlostFirebaseAuth.checkUserHasEmail();
      if (!hasEmail) {
        // Keep the email collection visible if email is still missing
        console.error("Email verification completed but user still has no email");
        setNeedsEmailVerification(true);
        return;
      }

      try {
        await getlostFirebaseAuth.establishAdminSession();
        // Session established with current user, go to home
        navigate("/");
        return;
      } catch (sessionErr: any) {
        const msg = sessionErr?.message ?? "";
        // If backend reports no profile, send user to onboarding
        if (msg.includes("404") && msg.includes("Profile not found")) {
          navigate("/onboarding");
          return;
        }
        console.error("Failed to establish admin session after email verification:", sessionErr);
        // Fallback: navigate home; app can retry session on next request
        navigate("/");
        return;
      }
    } catch (error) {
      console.error("Error verifying email status:", error);
      // On error, attempt navigation anyway
      navigate("/");
    }
  };

  const handleAuthError = (error: Error) => {
    console.error("Authentication error:", error);
  };

  const renderAuthContent = (content: React.ReactNode) => {
    // If user is already authenticated on register page, just show the onboarding form
    if (type === "register" && isAlreadyAuthenticated) {
      return null; // The OnboardingPage component will handle everything
    }
    
    // If user needs email verification, show email collection form
    if (needsEmailVerification) {
      return (
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          {/* Logo */}
          <Link to="/">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap="12px"
              marginBottom="32px"
            >
              <img src="images/getlost-logo.png" alt="GetLost Logo" height={72}/>
            </Box>
          </Link>
          
          {/* Email Collection Component */}
          <EmailCollectionForm onSuccess={handleEmailVerified} />
        </Box>
      );
    }
    
    return (
      <Box sx={{ width: "100%", maxWidth: 400 }}>
        {/* Logo */}
        <Link to="/">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="12px"
            marginBottom="32px"
          >
            <img src="images/getlost-logo.png" alt="GetLost Logo" height={72}/>
          </Box>
        </Link>
        
        {/* Title */}
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 3, 
            fontWeight: 500,
            textAlign: "center",
            color: "primary.main"
          }}
        >
          Sign in / up to your account
        </Typography>

        {/* Auth method toggle */}
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Button
            variant={authMode === "phone" ? "contained" : "outlined"}
            onClick={() => setAuthMode("phone")}
            fullWidth
            startIcon={<PhoneIcon />}
          >
            Phone
          </Button>
          <Button
            variant={authMode === "email" ? "contained" : "outlined"}
            onClick={() => setAuthMode("email")}
            fullWidth
            startIcon={<EmailIcon />}
          >
            Email
          </Button>
        </Box>
        
        {/* Phone or Email Authentication */}
        {authMode === "phone" ? (
          <PhoneAuth
            onSuccess={handleAuthSuccess}
            onError={handleAuthError}
            onNeedsOnboarding={handleNeedsOnboarding}
          />
        ) : (
          <EmailVerification
            onSuccess={handleAuthSuccess}
            onError={handleAuthError}
            onNeedsOnboarding={handleNeedsOnboarding}
          />
        )}
        
        {/* Divider */}
        <Box sx={{ my: 3, display: "flex", alignItems: "center" }}>
          <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
          <Typography variant="body2" sx={{ mx: 2, color: "text.secondary" }}>
            or
          </Typography>
          <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
        </Box>
        
        {/* Other provider buttons - always visible */}
        {content}
      </Box>
    );
  };

  const handleProviderClick = async (provider: string) => {
    if (provider === "phone") {
      setAuthMode("phone");
    } else if (provider === "email") {
      setAuthMode("email");
    }
  };

  return (
    <MUIAuthPage
      type={type}
      hideForm={true}
      wrapperProps={authWrapperProps}
      renderContent={renderAuthContent}
      contentProps={{
        sx: {
          "& .MuiCardContent-root > .MuiTypography-h5": {
            display: "none",
          },
        },
      }}
      formProps={formProps}
      providers={[
        {
          name: "google",
          label: "Continue with Google",
          icon: (
            <GoogleIcon
              style={{
                fontSize: 24,
              }}
            />
          ),
        },
        {
          name: "apple",
          label: "Continue with Apple",
          icon: (
            <AppleIcon
              style={{
                fontSize: 24,
              }}
            />
          ),
        }
      ]}
    />
  );
};
